import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import { Link } from "react-router-dom";

function Economics28() {
    const contentRef = useRef(null);
       const links = {
      prev: "/economics-27", 
      index: "/index-economics",                 
      next: "/economics-29", 
    };
      const titleText = "Value: A Subjective Perspective";
  const questions = [
  {
    text: "1. According to the Austrian School, what is the correct relationship between value and price?",
    options: [
      {
        value: "A",
        label: "A. Price is the objective measurement of a good's true value",
      },
      {
        value: "B",
        label: "B. Value determines price, and both are universally constant",
      },
      {
        value: "C",
        label: "C. Price is the result of subjective valuations during exchange",
      },
      {
        value: "D",
        label: "D. Price and value are always equal for both buyer and seller",
      },
    ],
    correct: "C",
  },
  {
    text: "2. Why do voluntary exchanges take place in a market?",
    options: [
      {
        value: "A",
        label:
          "A. Because both parties agree the items exchanged are objectively equal",
      },
      {
        value: "B",
        label:
          "B. Because each party values what they are receiving more than what they are giving up",
      },
      {
        value: "C",
        label:
          "C. Because the government assigns a value to each good or service",
      },
      {
        value: "D",
        label: "D. Because both parties are forced to make the trade",
      },
    ],
    correct: "B",
  },
  {
    text: "3. What is a major critique of the labor theory of value?",
    options: [
      {
        value: "A",
        label: "A. It correctly links prices to effort",
      },
      {
        value: "B",
        label:
          "B. It ignores consumer preferences and focuses only on production inputs",
      },
      {
        value: "C",
        label: "C. It supports dynamic pricing mechanisms",
      },
      {
        value: "D",
        label:
          "D. It explains market prices more accurately than subjective value theory",
      },
    ],
    correct: "B",
  },
  {
    text: "4. What role do market prices play in resource allocation?",
    options: [
      {
        value: "A",
        label:
          "A. They objectively determine which goods should be produced",
      },
      {
        value: "B",
        label:
          "B. They signal profit opportunities and help direct resources to valued uses",
      },
      {
        value: "C",
        label:
          "C. They eliminate the need for subjective decision-making",
      },
      {
        value: "D",
        label:
          "D. They are fixed and predictable across time and place",
      },
    ],
    correct: "B",
  },
];
  // State to track selected answers for all questions
    const [answers, setAnswers] = useState({});
  
    // Function to update an answer
    const handleAnswerChange = (questionIndex, selectedValue) => {
      setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
    };
  
    // Compute score
    const score = questions.reduce((total, q, index) => {
      return total + (answers[index] === q.correct ? 1 : 0);
    }, 0);

  return (
    <div className="container">
      <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={28}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 28</b>! Today, we dive into one of the most
        fundamental ideas in Austrian economics: the subjective nature of value.
        Unlike physical measurements or objective indicators, value in economics
        arises from personal judgment, individual context, and time-bound needs.
        By exploring how individuals assign worth to goods and services, we will
        better understand the roots of trade, the function of markets, and why
        production alone does not guarantee value creation. This lesson will
        help sharpen your ability to think clearly about what makes something
        valuable in the real world.
        <br />
        Economics often grapples with the nature of value. While common
        discussions tend to associate value directly with price, this connection
        is misleading and fails to capture the complex nature of human action in
        markets. At the heart of the Austrian School of economics lies a
        foundational principle: value is subjective. This idea forms the basis
        for understanding why exchanges happen, how prices emerge, and why some
        economic activities lead to prosperity while others waste resources.
        <br />
        <br />
        <b>Value and Price Are Not the Same</b>
        <br />
        <br /> Many misunderstandings in economics stem from the confusion
        between the concepts of value and price. Price is often taken as a
        direct representation of a good's value, but this assumption does not
        withstand scrutiny. A price is simply the amount of money exchanged for
        a good or service at a specific point in time. It reflects the outcome
        of an interaction between a buyer and a seller, not a universal or
        intrinsic measurement of worth. When a transaction occurs, it means the
        seller believes the money received is worth more than the good being
        sold, while the buyer believes the good is worth more than the money
        spent. This mutual agreement does not reveal exact valuations from
        either side, but only that a range of subjective preferences intersected
        at a moment to allow the exchange. This divergence shows that prices
        function more like indicators of past decisions rather than static
        markers of a good's importance. They tell us what happened, not why it
        happened or what may happen next. Each party in a transaction operates
        based on personal preferences, goals, and circumstances, making any
        objective valuation impossible. <br />
        <br />
        <b>The Nature of Free Exchange</b> <br />
        <br />
        Whenever two individuals voluntarily decide to exchange goods or
        services, the foundation of that interaction is mutual benefit. Each
        party expects to receive something of greater personal value than what
        they are offering. This can only make sense if one accepts that value is
        inherently subjective. Suppose someone trades a book for a piece of
        fruit. To the person offering the book, the fruit is of greater value in
        that moment, perhaps due to hunger or preference. To the individual
        providing the fruit, the book holds greater appeal. If value were
        objective and universal, such exchanges would not be rational. No one
        would willingly part with an objectively more valuable item for one of
        lesser value. This understanding is central to trade. Exchanges do not
        occur because of uniform valuations but because of differing priorities
        and assessments between individuals. Without subjectivity, trade loses
        its rational foundation.
        <br />
        <br />
        <b>Austrian View vs. Other Schools of Thought </b>
        <br />
        <br />
        The Austrian School's emphasis on the subjective nature of value
        contrasts sharply with the frameworks employed by many mainstream
        economic theories. Several modern schools attempt to treat value as
        something measurable and constant, using imaginary units or formulas to
        assign it fixed attributes. One such concept is the use of 'utils' to
        measure utility. Despite their frequent appearance in textbooks and
        theoretical models, utils are undefined and unquantifiable. There exists
        no instrument or method by which utility can be measured in the real
        world, making the use of such constructs a purely academic exercise.
        Some economists have gone a step further, directly translating value
        into monetary figures. This approach overlooks the essential question of
        why individuals engage in trade. If two items were truly equal in
        monetary value for both parties, there would be no motivation for an
        exchange. The decision to trade relies not on objective parity, but on
        subjective discrepancies in valuation. <br />
        <br />
        <b>Critique of the Labor Theory of Value</b>
        <br />
        <br /> Among the most widely debated theories of value is the labor
        theory, most famously associated with Marxist thought. According to this
        theory, the value of a good is determined by the amount of labor
        required to produce it. At first glance, the notion appears to have
        merit. All economic goods seem to involve labor in their production or
        acquisition. Even natural goods require effort to make them usable.
        Fruits growing on a tree do not nourish until someone picks and consumes
        them. Because labor appears to be a common element in the creation of
        all goods, it is tempting to attribute value directly to the amount of
        labor involved. However, this theory fails upon closer inspection. Just
        because something requires effort does not mean it is valuable. One
        could spend hours crafting a sculpture from mud, but if no one desires
        it, the labor is irrelevant to its market value. The central mistake of
        the labor theory is its focus on inputs rather than outcomes. It assumes
        that work automatically translates into worth, which is demonstrably
        untrue. Markets provide the ultimate test of value. If a good cannot
        attract a buyer at a given price, it has little economic value,
        regardless of how much labor was expended. Consumers do not assess
        products based on the time or difficulty involved in production. Their
        concern lies with the utility they derive from ownership or use. Labor
        becomes valuable only when it results in something that others want. An
        efficient production process is not judged by the intensity of the labor
        it demands, but by whether it yields goods or services that satisfy
        human needs better than alternatives. The market, through prices,
        reveals which production efforts align with actual demand. <br />
        <br />
        <b>How Value Is Determined</b>
        <br />
        <br /> Austrian economists argue that value does not reside in goods
        themselves but emerges from the preferences of individuals at specific
        moments in time. Context plays a crucial role. The same item may have
        drastically different value to the same person depending on the
        situation. Imagine a bottle of water. In a well-stocked home, it might
        seem trivial. In a desert, it could be seen as a life-saving necessity.
        The value is not inherent in the water itself but is instead derived
        from the conditions and the needs of the person assessing it. This
        viewpoint also accounts for the diminishing marginal utility of goods.
        The more of a good someone possesses, the less additional value each new
        unit typically provides. A person with ten loaves of bread will not
        value the eleventh loaf as highly as someone who has none. Thus,
        subjective value changes not only across individuals but within the same
        individual over time. This constant fluctuation makes objective
        measurement impossible. There is no universal scale to compare the worth
        of different goods. Each valuation is personal, dynamic, and based on
        circumstances. <br />
        <br />
        <b>Role of the Market in Signaling Value</b>
        <br />
        <br /> Though value itself is subjective, markets aggregate countless
        individual valuations through the price mechanism. When prices emerge,
        they serve as rough signals of where supply meets demand. However, these
        prices should not be mistaken as objective indicators of value. Prices
        reflect the marginal valuations of those at the edge of deciding to buy
        or sell. They do not encapsulate the entire range of subjective
        evaluations that exist within the market. Moreover, they are always
        bound to a particular time and place. A good that commands a high price
        in one city may be nearly worthless in another due to differences in
        demand, availability, or consumer preference. Market prices help
        coordinate production and consumption. Entrepreneurs and producers
        respond to price signals to allocate resources efficiently. If the cost
        of inputs is less than the expected price of outputs, producers are
        incentivized to continue. If not, they must adapt or risk loss. In this
        sense, the market functions as a decentralized feedback system. Success
        in the marketplace implies that the production process has created
        something others find valuable. Failure signals the opposite.
        <br />
        <br />
        <b>Implications for Production and Resource Allocation</b>
        <br />
        <br /> Production is not inherently beneficial. It becomes so only when
        it creates something others desire. The mere expenditure of time and
        energy is insufficient to justify continued effort. Market feedback
        ensures that resources are directed toward satisfying human needs as
        efficiently as possible. When a producer discovers that the selling
        price of a product exceeds the combined cost of inputs, it reveals that
        the chosen production method delivers something society values more than
        the resources consumed. Conversely, a production method that yields
        losses suggests misallocation. Resources are being used in ways that do
        not reflect the preferences of consumers. This distinction is essential
        to understanding economic growth. Profit and loss are not just
        bookkeeping entries; they are signals. Profit indicates that production
        is aligned with consumer desires. Loss indicates the opposite. The
        discipline of the market encourages better decision-making and
        innovation. <br />
        <br />
        <b>The Subjective Theory of Value and Human Action</b>
        <br />
        <br /> Understanding value as subjective provides a consistent and
        realistic framework for interpreting human action in the economy. Every
        decision, from purchasing a coffee to launching a new business, is based
        on personal judgments about what is preferable at a given time. Scarcity
        forces choices. People rank their preferences and act to satisfy the
        most urgent unmet need. These choices are influenced by countless
        factors, from mood and environment to past experience and future
        expectations. No model can fully capture these nuances, but the
        recognition of subjectivity allows us to acknowledge and respect them.
        The subjective theory of value also explains why markets are dynamic. As
        conditions change, so do individual preferences. This fluidity makes
        centralized economic planning ineffective. No authority can anticipate
        or replicate the constantly shifting landscape of individual valuation
        across an entire population. <br />
        <br />
        The Austrian School's understanding of value as a subjective phenomenon
        offers a powerful lens through which to view economic behavior. It
        clarifies why prices fluctuate, why people trade, and how resources are
        allocated in a market economy. Rejecting the flawed assumptions of
        objective value leads to more accurate analysis and better policy
        decisions. Markets are not perfect, but they are the most effective
        means humans have discovered to express, coordinate, and respond to the
        infinite variety of individual needs and preferences. Recognizing value
        as subjective is not just an academic exercise, rather it is fundamental to
        understanding the world of economics and the actions of the people
        within it.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today, we explored the concept of subjective value in Austrian
        economics. We learned that value is not inherent in goods or determined
        by labor, but instead arises from individual preferences and context.
        Prices reflect market interactions, not fixed measurements of worth. We
        also saw how profit and loss signal the usefulness of production in
        satisfying human needs. This understanding helps explain the nature of
        trade, the role of prices, and the limitations of central planning.
        Embracing the subjectivity of value equips us with a deeper and more
        human-centered understanding of economics.
        <br />
        <br />
        <b>Coming Up Next</b>
        <br />
        <br /> 
In the next lesson, we will introduce Marginalism, a breakthrough in economic thinking that explains how people actually make choices in the real world. You will learn why decisions are made at the margin rather than in totals, how value is subjective and context-dependent, and how this insight resolves classic puzzles like the water–diamond paradox. This lesson will prepare you to understand prices, trade-offs, and everyday decision-making with greater clarity and precision.

      </p>
      <h2>Test your understanding</h2>
       {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>

     
        
       <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />

      
    </div>
  );
}

export default Economics28;
